import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Download,
  Info,
  Maximize2,
  Presentation,
  RefreshCw,
  X
} from 'lucide-react';
import {
  buildExecutiveSummaryModel,
  buildExecutiveInstanceCounts,
  classifyExecutiveUpdateCategory,
  type ExecutiveInstanceRow,
  type ExecutiveSummaryModel,
  type ExecutiveVPRow
} from '../../lib/operational-control/executive-summary-model';
import {
  EXECUTIVE_LABELS,
  EXECUTIVE_LEGEND_STATES,
  EXECUTIVE_STATE_PRESENTATION,
  EXECUTIVE_TOOLTIPS,
  classifyMetricException,
  classifyOperationalStatus,
  classifyPerformance,
  describeStale,
  resolveOperationalSettings,
  type ExecutiveSemanticPresentation,
  type ExecutiveSemanticState
} from '../../lib/operational-control/executive-summary-semantics';
import {
  OPERATIONAL_MONTHS,
  OPERATIONAL_MONTH_LABELS,
  parseSelectedMonthIndex
} from '../../lib/operational-control/operational-start-period-config';
import type { OperationalControlSettings } from '../../lib/operational-control/operational-control-settings';
import type {
  OperationalControlReadModel,
  OperationalStatus
} from '../../lib/operational-control/types';
import { ExecutiveOperationalSummaryPdf } from './ExecutiveOperationalSummaryPdf';
import {
  EXECUTIVE_PDF_LAYOUT,
  calculateExecutivePdfPlacement,
  prepareExecutivePdfClone,
  validateExecutivePdfSurface,
  waitForExecutivePdfSurface
} from '../../lib/operational-control/executive-pdf-layout';

type Props = {
  data: OperationalControlReadModel;
  user: any;
  selectedMonth: string;
  onSelectedMonthChange: (month: string) => void;
  onRefresh: () => Promise<void>;
};

const APP_VERSION = 'v18.27.69-CONTROL-EXECUTIVE-SUMMARY-RC8';

const UPDATE_FILTER_LABEL = {
  UPDATED: 'Actualizadas',
  STALE: 'Rezago',
  EXCEPTION: 'Excepción'
} as const;

const cn = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');
const formatPct = (value: number | null) => value === null ? 'N/A' : `${Math.round(value)}%`;
const criticalAlertLabel = (count: number) => count === 0 ? '—' : `${count} ${count === 1 ? 'crítica' : 'críticas'}`;
const SemanticBadge = ({
  state,
  label,
  title,
  presentation
}: {
  state: ExecutiveSemanticState;
  label?: string;
  title?: string;
  presentation?: ExecutiveSemanticPresentation;
}) => {
  const visual = presentation ?? EXECUTIVE_STATE_PRESENTATION[state];
  const visibleLabel = label ?? visual.label;
  const description = title ?? visual.description;
  return (
    <span
      data-semantic-state={state}
      title={description}
      aria-label={`${visibleLabel}. ${description}`}
      className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[9px] font-black uppercase tracking-wide', visual.badgeClass)}
    >
      <span aria-hidden="true" className="text-[10px]">{visual.indicator}</span>
      {visibleLabel}
    </span>
  );
};

const MetricValue = ({
  value,
  metric,
  tooltip,
  settings,
  status,
  prominent = false
}: {
  value: number | null;
  metric: 'plan' | 'indicators' | 'performance' | 'capture';
  tooltip: string;
  settings: OperationalControlSettings;
  status?: OperationalStatus;
  prominent?: boolean;
}) => {
  const state = classifyMetricException(value, status, metric, settings);
  const visual = EXECUTIVE_STATE_PRESENTATION[state];
  if (value === null) return <SemanticBadge state={state} title={tooltip} />;
  return (
    <span
      title={`${visual.label}. ${tooltip}`}
      aria-label={`${formatPct(value)}. ${visual.label}. ${tooltip}`}
      className={cn('inline-flex items-center gap-1.5 font-black tabular-nums', prominent ? 'text-3xl' : 'text-sm', visual.textClass)}
    >
      <span aria-hidden="true" className={prominent ? 'text-base' : 'text-[10px]'}>{visual.indicator}</span>
      {formatPct(value)}
      <span className="sr-only">{visual.label}</span>
    </span>
  );
};

const NationalCard = ({
  label,
  value,
  state,
  tooltip,
  children
}: {
  label: string;
  value?: string | number;
  state: ExecutiveSemanticState;
  tooltip: string;
  children?: React.ReactNode;
}) => {
  const visual = EXECUTIVE_STATE_PRESENTATION[state];
  return (
    <article title={tooltip} aria-label={`${label}. ${value ?? ''}. ${visual.label}. ${tooltip}`} className={cn('min-h-[118px] rounded-xl border p-3.5', visual.cardClass)}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-300">{label}</div>
        <span aria-hidden="true" className={cn('text-sm font-black', visual.textClass)}>{visual.indicator}</span>
      </div>
      {children ?? (
        <>
          <div className={cn('mt-3 text-3xl font-black leading-none tabular-nums', visual.textClass)}>{value}</div>
          <div className="mt-3"><SemanticBadge state={state} /></div>
        </>
      )}
    </article>
  );
};

const ExecutiveLegend = () => (
  <section data-executive-legend className="rounded-lg border border-slate-800 bg-black/30 px-3 py-2.5" aria-label="Leyenda ejecutiva">
    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-1 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-400"><Info size={12} /> Leyenda</span>
      {EXECUTIVE_LEGEND_STATES.map((state) => <SemanticBadge key={state} state={state} />)}
    </div>
  </section>
);

const NationalOverview = ({ model, settings }: { model: ExecutiveSummaryModel; settings: OperationalControlSettings }) => {
  const healthState = classifyPerformance(model.national.healthScore, settings);
  const planState = classifyPerformance(model.national.planProgress, settings);
  const indicatorState = classifyPerformance(model.national.kpiCompliance, settings);
  const captureState = classifyPerformance(model.national.capturePct, settings);
  const staleVisual = describeStale(model.national.staleInstances, settings);
  return (
    <section data-national-cards className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8">
      <NationalCard label={EXECUTIVE_LABELS.health} value={formatPct(model.national.healthScore)} state={healthState} tooltip={EXECUTIVE_TOOLTIPS.health} />
      <NationalCard label={EXECUTIVE_LABELS.vicepresidencies} value={model.national.visibleVicepresidencies} state="NEUTRAL" tooltip={EXECUTIVE_TOOLTIPS.vicepresidencies} />
      <NationalCard label={EXECUTIVE_LABELS.instances} value={model.national.visibleInstances} state="NEUTRAL" tooltip={EXECUTIVE_TOOLTIPS.instances} />
      <NationalCard label={EXECUTIVE_LABELS.plan} value={formatPct(model.national.planProgress)} state={planState} tooltip={EXECUTIVE_TOOLTIPS.plan} />
      <NationalCard label={EXECUTIVE_LABELS.indicators} value={formatPct(model.national.kpiCompliance)} state={indicatorState} tooltip={EXECUTIVE_TOOLTIPS.indicators} />
      <NationalCard label={EXECUTIVE_LABELS.capture} value={formatPct(model.national.capturePct)} state={captureState} tooltip={EXECUTIVE_TOOLTIPS.capture} />
      <NationalCard label={EXECUTIVE_LABELS.alerts} state={model.national.criticalInstances > 0 ? 'CRITICAL' : 'NEUTRAL'} tooltip="Conteo de alertas críticas.">
        <div className="mt-3 flex min-h-8 items-center justify-center">
          <span data-executive-critical-alert-count="national" aria-label={criticalAlertLabel(model.national.criticalInstances)} className={cn('text-xl font-black tabular-nums', model.national.criticalInstances > 0 ? 'text-red-300' : 'text-slate-400')}>
            {criticalAlertLabel(model.national.criticalInstances)}
          </span>
        </div>
      </NationalCard>
      <NationalCard label={EXECUTIVE_LABELS.exceptions} state={model.national.noConfigurationInstances > 0 ? 'NOT_CONFIGURED' : model.national.onboardingInstances > 0 ? 'STARTUP' : model.national.staleInstances > 0 ? 'STALE' : 'OPTIMAL'} tooltip={EXECUTIVE_TOOLTIPS.exceptions}>
        <div className="mt-3 flex flex-col items-start gap-1.5">
          <SemanticBadge state={model.national.staleInstances > 0 ? 'STALE' : 'OPTIMAL'} presentation={staleVisual} label={`${model.national.staleInstances} Rezago`} title={EXECUTIVE_TOOLTIPS.stale} />
          <SemanticBadge state={model.national.onboardingInstances > 0 ? 'STARTUP' : 'OPTIMAL'} label={`${model.national.onboardingInstances} Arranque`} title={EXECUTIVE_TOOLTIPS.startup} />
          <SemanticBadge state={model.national.noConfigurationInstances > 0 ? 'NOT_CONFIGURED' : 'OPTIMAL'} label={`${model.national.noConfigurationInstances} Sin configurar`} title={EXECUTIVE_TOOLTIPS.notConfigured} />
        </div>
      </NationalCard>
    </section>
  );
};

const StatusCell = ({ row, settings }: { row: ExecutiveInstanceRow; settings: OperationalControlSettings }) => {
  const state = classifyOperationalStatus(row.operationalStatus);
  const alerts = row.alerts.map((alert) => alert.message).join(' ');
  const title = alerts || EXECUTIVE_STATE_PRESENTATION[state].description;
  return (
    <div className="flex max-w-[180px] flex-wrap gap-1" title={title} aria-label={title}>
      <SemanticBadge state={state} />
      {row.staleMonthsOperational > 0 && (
        <SemanticBadge
          state="STALE"
          presentation={describeStale(row.staleMonthsOperational, settings)}
          label="Rezago"
          title={`${EXECUTIVE_TOOLTIPS.stale} ${row.staleMonthsOperational} mes(es).`}
        />
      )}
    </div>
  );
};

export const IndicatorEvaluation = ({ display, status, settings }: {
  display: ExecutiveVPRow['indicatorDisplay'];
  status: OperationalStatus;
  settings: OperationalControlSettings;
}) => {
  const value = display?.closedValue ?? null;
  const detail = [display?.closedPeriod, display?.monthlyPeriod, display?.monthlyReason].filter(Boolean).join(' · ');
  const state = classifyMetricException(value, status, 'indicators', settings);
  if (value === null && /sin indicadores configurados|sin configuraci[oó]n/i.test(display?.monthlyReason || '')) {
    return <SemanticBadge state="NOT_CONFIGURED" label="Sin configurar" title={detail || EXECUTIVE_TOOLTIPS.notConfigured} />;
  }
  if (value === null) return <span className="text-sm font-bold text-slate-500" title={detail || 'Sin evaluación acumulada válida'} aria-label="Sin evaluación acumulada">—</span>;
  const visual = EXECUTIVE_STATE_PRESENTATION[state];
  return <span className={cn('inline-flex items-center gap-1.5 text-sm font-black tabular-nums', visual.textClass)} title={`Acumulado cerrado ${display?.closedPeriod || ''}. ${detail}`} aria-label={`${formatPct(value)} acumulado cerrado`}><span aria-hidden="true" className="text-[10px]">{visual.indicator}</span>{formatPct(value)}</span>;
};

const InstanceTable = ({ vp, settings }: { vp: ExecutiveVPRow; settings: OperationalControlSettings }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-800" data-pdf-overflow>
    <table data-executive-instance-table data-pdf-table className="w-full min-w-[900px] table-fixed text-left text-xs">
      <thead className="bg-slate-900 text-[8px] font-black uppercase tracking-widest text-slate-400">
        <tr>
          <th className="w-[28%] px-3 py-2">Instancia</th>
          <th className="w-[11%] px-3 py-2">Tipo</th>
          <th className="w-[11%] px-3 py-2">Plan</th>
          <th className="w-[12%] px-3 py-2">Indicadores</th>
          <th className="w-[13%] px-3 py-2">Desempeño</th>
          <th className="w-[10%] px-3 py-2">Captura</th>
          <th className="w-[15%] px-3 py-2">Estado</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-800">
        {vp.instances.map((row) => {
          const captureState = row.captureApplicable
            ? (row.currentMonthCapture ? 'OPTIMAL' : 'NO_CAPTURE')
            : 'NO_OBLIGATION';
          return (
            <tr key={row.instanceId} data-instance-id={row.instanceId} className="align-top">
              <td className="px-3 py-2.5">
                <strong className="block text-xs text-white">{row.instanceName}</strong>
                <small className="mt-0.5 block text-[9px] text-slate-400" title="Titular de la instancia">{row.instancePresident}</small>
              </td>
              <td className="px-3 py-2.5 text-[10px] font-bold text-slate-300">{row.instanceType}</td>
              <td className="px-3 py-2.5"><MetricValue value={row.planProgress} metric="plan" tooltip={EXECUTIVE_TOOLTIPS.plan} settings={settings} status={row.operationalStatus} /></td>
              <td className="px-3 py-2.5"><IndicatorEvaluation display={row.indicatorDisplay} status={row.operationalStatus} settings={settings} /></td>
              <td className="px-3 py-2.5"><MetricValue value={row.weightedPerformance} metric="performance" tooltip={EXECUTIVE_TOOLTIPS.performance} settings={settings} status={row.operationalStatus} /></td>
              <td className="px-3 py-2.5"><SemanticBadge state={captureState} label={row.captureApplicable && row.currentMonthCapture ? 'Óptimo' : undefined} title={row.captureApplicable ? (row.currentMonthCapture ? EXECUTIVE_TOOLTIPS.capture : EXECUTIVE_TOOLTIPS.noCapture) : EXECUTIVE_STATE_PRESENTATION[captureState].description} /></td>
              <td className="px-3 py-2.5"><StatusCell row={row} settings={settings} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const VicePresidencyTable = ({
  vicePresidencies,
  settings,
  expanded,
  onToggle,
  onSelect
}: {
  vicePresidencies: ExecutiveVPRow[];
  settings: OperationalControlSettings;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onSelect?: (index: number) => void;
}) => (
  <section className="overflow-hidden rounded-xl border border-slate-800" aria-label="Desempeño por vicepresidencia">
    <div className="overflow-x-auto" data-pdf-overflow>
      <table data-executive-vicepresidency-table data-pdf-table className="w-full min-w-[980px] table-fixed border-collapse text-left">
        <thead className="bg-slate-900 text-[9px] font-black uppercase tracking-widest text-slate-400">
          <tr>
            <th className="w-[25%] px-4 py-2.5">Vicepresidencia</th>
            <th className="w-[16%] px-3 py-2.5">Inst.</th>
            <th className="w-[10%] px-3 py-2.5">Plan</th>
            <th className="w-[12%] px-3 py-2.5">Indicadores</th>
            <th className="w-[12%] px-3 py-2.5">Desempeño</th>
            <th className="w-[10%] px-3 py-2.5">Captura</th>
            <th className="w-[15%] px-3 py-2.5">Alertas</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {vicePresidencies.map((vp, index) => {
            const key = vp.vpId || 'NO_VP';
            const isOpen = expanded.has(key);
            return (
              <React.Fragment key={key}>
                <tr data-vp-id={vp.vpId || undefined} className="bg-slate-950/80 hover:bg-slate-900/70">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {!onSelect && <button data-pdf-control onClick={() => onToggle(key)} aria-expanded={isOpen} aria-label={`${isOpen ? 'Ocultar' : 'Mostrar'} instancias de ${vp.vpName}`} className="inline-flex shrink-0 rounded-md border border-slate-700 p-1 text-slate-300">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button>}
                      <button onClick={() => onSelect ? onSelect(index) : onToggle(key)} className="min-w-0 text-left">
                        <strong className="block text-xs text-white">{vp.vpName}</strong>
                        <small className="mt-0.5 block text-[9px] text-slate-400">{vp.presidentName}</small>
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5" title={`${vp.instanceCounts.total} total · ${vp.instanceCounts.updated} actualizadas · ${vp.instanceCounts.stale} rezago · ${vp.instanceCounts.exception} excepción`}>
                    <strong className="block text-xs font-black text-slate-100">{vp.instanceCounts.total} total</strong>
                    <span className="mt-1 flex whitespace-nowrap gap-x-1 text-[8px] font-bold leading-tight">
                      <span className="text-emerald-300">{vp.instanceCounts.updated} actualizadas</span><span className="text-slate-600">·</span>
                      <span className="text-amber-300">{vp.instanceCounts.stale} rezago</span><span className="text-slate-600">·</span>
                      <span className="text-sky-300">{vp.instanceCounts.exception} excepción</span>
                    </span>
                  </td>
                  <td className="px-3 py-2.5"><MetricValue value={vp.planProgress} metric="plan" tooltip={EXECUTIVE_TOOLTIPS.plan} settings={settings} status={vp.status} /></td>
                  <td className="px-3 py-2.5"><IndicatorEvaluation display={vp.indicatorDisplay} status={vp.status} settings={settings} /></td>
                  <td className="px-3 py-2.5"><MetricValue value={vp.weightedPerformance} metric="performance" tooltip={EXECUTIVE_TOOLTIPS.performance} settings={settings} status={vp.status} /></td>
                  <td className="px-3 py-2.5"><MetricValue value={vp.capturePct} metric="capture" tooltip={EXECUTIVE_TOOLTIPS.capture} settings={settings} status={vp.captureApplicableCount ? vp.status : 'SIN_OBLIGACION'} /></td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex min-h-7 items-center justify-center whitespace-nowrap" title={`${vp.criticalCount} alertas críticas`}>
                      <span data-executive-critical-alert-count="vicepresidencia" aria-label={criticalAlertLabel(vp.criticalCount)} className={cn('text-xs font-black tabular-nums', vp.criticalCount > 0 ? 'text-red-300' : 'text-slate-400')}>
                        {criticalAlertLabel(vp.criticalCount)}
                      </span>
                    </div>
                  </td>
                </tr>
                {!onSelect && (
                  <tr data-pdf-instance-details className={isOpen ? '' : 'hidden'}>
                    <td colSpan={7} className="bg-black/40 px-4 py-3"><InstanceTable vp={vp} settings={settings} /></td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  </section>
);

const DecisionCard = ({
  title,
  rows,
  empty
}: {
  title: string;
  rows: Array<{ title: string; subtitle: string; state: ExecutiveSemanticState; badge: string; tooltip: string }>;
  empty: string;
}) => (
  <section className="rounded-xl border border-slate-800 bg-slate-950 p-3.5">
    <h3 className="text-[9px] font-black uppercase tracking-widest text-slate-300">{title}</h3>
    <div className="mt-2 space-y-1.5">
      {rows.length === 0 ? <p className="text-[10px] font-bold text-slate-500">{empty}</p> : rows.map((row, index) => (
        <div key={`${row.title}-${index}`} title={row.tooltip} className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-black/40 px-2.5 py-2">
          <div className="min-w-0"><div className="truncate text-[10px] font-black text-white" title={row.title}>{row.title}</div><div className="truncate text-[9px] text-slate-400" title={row.subtitle}>{row.subtitle}</div></div>
          <SemanticBadge state={row.state} label={row.badge} title={row.tooltip} />
        </div>
      ))}
    </div>
  </section>
);

const scopeLabel = (user: any) => {
  const role = String(user?.role || '').toUpperCase();
  if (['SUPER_ADMIN', 'ADMINISTRADOR', 'DIRECTOR_NACIONAL'].includes(role)) return 'Nacional autorizado';
  if (user?.vicepresidency_id) return 'Vicepresidencia autorizada';
  if (user?.instance_id || user?.commission_id) return 'Instancia autorizada';
  return 'Alcance autorizado';
};

const ExecutiveOperationalSummary = ({ data, user, selectedMonth, onSelectedMonthChange, onRefresh }: Props) => {
  const model = useMemo(() => buildExecutiveSummaryModel(data), [data]);
  const executiveInstances = useMemo(() => model.vicePresidencies.flatMap((vp) => vp.instances), [model.vicePresidencies]);
  const settings = useMemo(() => resolveOperationalSettings(data.opSettings), [data.opSettings]);
  const [vpFilter, setVpFilter] = useState('ALL');
  const [updateFilter, setUpdateFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [presentationMode, setPresentationMode] = useState(false);
  const [presentationVpIndex, setPresentationVpIndex] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  const filteredVicePresidencies = useMemo(() => model.vicePresidencies
    .filter((vp) => vpFilter === 'ALL' || (vp.vpId || 'NO_VP') === vpFilter)
    .map((vp) => {
      const instances = vp.instances.filter((row) =>
        (updateFilter === 'ALL' || classifyExecutiveUpdateCategory(row) === updateFilter) &&
        (typeFilter === 'ALL' || row.instanceType === typeFilter)
      );
      return { ...vp, instances, instanceCount: instances.length, instanceCounts: buildExecutiveInstanceCounts(instances) };
    })
    .filter((vp) => updateFilter === 'ALL' && typeFilter === 'ALL' ? true : vp.instances.length > 0),
  [model.vicePresidencies, updateFilter, typeFilter, vpFilter]);

  const instanceTypes = useMemo(() => Array.from(new Set(executiveInstances.map((row) => row.instanceType))).sort(), [executiveInstances]);
  const activePresentationVp = presentationVpIndex === null ? null : filteredVicePresidencies[presentationVpIndex] || null;
  const focusLabel = [
    vpFilter === 'ALL' ? 'Todas las vicepresidencias' : filteredVicePresidencies[0]?.vpName || 'Vicepresidencia',
    updateFilter === 'ALL' ? null : UPDATE_FILTER_LABEL[updateFilter as keyof typeof UPDATE_FILTER_LABEL],
    typeFilter === 'ALL' ? null : typeFilter
  ].filter(Boolean).join(' / ');

  const toggleExpanded = (id: string) => setExpanded((current) => {
    const next = new Set(current);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  useEffect(() => {
    if (!presentationMode) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPresentationMode(false);
        setPresentationVpIndex(null);
        if (document.fullscreenElement) void document.exitFullscreen();
      }
      if (event.key === 'ArrowRight') {
        setPresentationVpIndex((current) => current === null ? 0 : Math.min(current + 1, filteredVicePresidencies.length - 1));
      }
      if (event.key === 'ArrowLeft') {
        setPresentationVpIndex((current) => current === null || current <= 0 ? null : current - 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [filteredVicePresidencies.length, presentationMode]);

  useEffect(() => {
    if (presentationVpIndex !== null && presentationVpIndex >= filteredVicePresidencies.length) {
      setPresentationVpIndex(filteredVicePresidencies.length ? filteredVicePresidencies.length - 1 : null);
    }
  }, [filteredVicePresidencies.length, presentationVpIndex]);

  const requestFullscreen = async () => {
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
  };

  const exportPdf = async () => {
    if (!pdfRef.current) return;
    setExporting(true);
    setExportError(null);
    try {
      const pdfRoot = pdfRef.current;
      await waitForExecutivePdfSurface(pdfRoot);
      const surface = validateExecutivePdfSurface(pdfRoot);
      const canvas = await html2canvas(pdfRoot, {
        foreignObjectRendering: EXECUTIVE_PDF_LAYOUT.foreignObjectRendering,
        scale: EXECUTIVE_PDF_LAYOUT.scale,
        useCORS: true,
        backgroundColor: EXECUTIVE_PDF_LAYOUT.backgroundColor,
        width: surface.width,
        height: surface.height,
        windowWidth: surface.width,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        onclone: prepareExecutivePdfClone
      });
      const pdf = new jsPDF({ orientation: EXECUTIVE_PDF_LAYOUT.orientation, unit: 'mm', format: EXECUTIVE_PDF_LAYOUT.format, compress: true });
      pdf.setProperties({ title: 'Gobernanza Institucional - Resumen Ejecutivo', subject: `Control Operativo ${selectedMonth} ${data.year}`, author: 'IAPRIORI', creator: APP_VERSION });
      const placement = calculateExecutivePdfPlacement(canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/png');
      pdf.addImage(imageData, 'PNG', placement.xMm, placement.yMm, placement.widthMm, placement.heightMm, undefined, 'FAST');
      if (pdf.getNumberOfPages() !== 1) throw new Error('PDF_PAGE_COUNT_INVALID');
      const monthCode = OPERATIONAL_MONTHS[parseSelectedMonthIndex(selectedMonth)];
      pdf.save(`Gobernanza_Institucional_Resumen_Ejecutivo_${monthCode}_${data.year}.pdf`);
    } catch (error: any) {
      console.error('[ExecutiveSummary] PDF export error', error);
      setExportError('No fue posible generar el PDF ejecutivo. Verifica el navegador e intenta nuevamente.');
    } finally {
      setExporting(false);
    }
  };

  if (data.rows.length === 0) {
    const role = String(user?.role || '').toUpperCase();
    const national = ['SUPER_ADMIN', 'ADMINISTRADOR', 'DIRECTOR_NACIONAL'].includes(role);
    return (
      <div className="rounded-2xl border border-slate-800 bg-slate-950 p-10 text-center">
        <AlertTriangle className="mx-auto text-amber-300" size={34} />
        <h2 className="mt-4 text-xl font-black uppercase text-white">{national ? 'Sin registros' : 'Sin alcance'}</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm font-semibold text-slate-400" title={national ? 'La lectura autorizada no devolvió instancias para el periodo.' : 'No existen instancias dentro del alcance autorizado.'}>{national ? 'Sin información del periodo' : 'Sin información autorizada'}</p>
      </div>
    );
  }

  const presentationHeader = (
    <div className="flex items-start justify-between gap-6 border-b border-slate-800 pb-4">
      <div>
        <div className="text-[11px] font-black uppercase tracking-[0.28em] text-sky-300">Gobernanza Institucional</div>
        <h1 className="mt-2 text-4xl font-black uppercase tracking-tight text-white">Control Operativo / Resumen Ejecutivo</h1>
        <div className="mt-2 text-sm font-bold text-slate-300">Periodo {OPERATIONAL_MONTH_LABELS[OPERATIONAL_MONTHS[parseSelectedMonthIndex(selectedMonth)]]} {data.year} · {scopeLabel(user)} · {focusLabel}</div>
      </div>
      <div className="text-right text-xs font-bold text-slate-400">{APP_VERSION}<br />Desarrollado por IAPRIORI</div>
    </div>
  );

  if (presentationMode) {
    return (
      <div className="fixed inset-0 z-[250] overflow-y-auto bg-slate-950 p-6 text-white lg:p-9">
        <div className="mx-auto max-w-[1900px]">
          {presentationHeader}
          {activePresentationVp === null ? (
            <div className="mt-5 space-y-4">
              <NationalOverview model={model} settings={settings} />
              <ExecutiveLegend />
              <VicePresidencyTable vicePresidencies={filteredVicePresidencies} settings={settings} expanded={new Set()} onToggle={() => undefined} onSelect={setPresentationVpIndex} />
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
                <div><div className="text-xs font-black uppercase tracking-widest text-sky-300">Detalle de vicepresidencia</div><h2 className="mt-2 text-4xl font-black text-white">{activePresentationVp.vpName}</h2><p className="mt-1 text-lg font-bold text-slate-300">{activePresentationVp.presidentName}</p></div>
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
                  <div><div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Plan</div><div className="mt-2"><MetricValue value={activePresentationVp.planProgress} metric="plan" tooltip={EXECUTIVE_TOOLTIPS.plan} settings={settings} status={activePresentationVp.status} prominent /></div></div>
                  <div><div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Indicadores</div><div className="mt-2"><IndicatorEvaluation display={activePresentationVp.indicatorDisplay} status={activePresentationVp.status} settings={settings} /></div></div>
                  <div><div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Desempeño</div><div className="mt-2"><MetricValue value={activePresentationVp.weightedPerformance} metric="performance" tooltip={EXECUTIVE_TOOLTIPS.performance} settings={settings} status={activePresentationVp.status} prominent /></div></div>
                  <div><div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Captura</div><div className="mt-2"><MetricValue value={activePresentationVp.capturePct} metric="capture" tooltip={EXECUTIVE_TOOLTIPS.capture} settings={settings} status={activePresentationVp.captureApplicableCount ? activePresentationVp.status : 'SIN_OBLIGACION'} prominent /></div></div>
                </div>
              </div>
              <ExecutiveLegend />
              <InstanceTable vp={activePresentationVp} settings={settings} />
            </div>
          )}

          <div data-pdf-control className="sticky bottom-0 mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-slate-800 bg-slate-950/95 py-4 backdrop-blur">
            <div className="flex gap-2">
              <button onClick={() => setPresentationVpIndex((current) => current === null || current <= 0 ? null : current - 1)} className="rounded-lg border border-slate-700 px-4 py-2 text-xs font-black uppercase"><ArrowLeft className="inline" size={14} /> Anterior</button>
              <button onClick={() => setPresentationVpIndex((current) => current === null ? 0 : Math.min(current + 1, filteredVicePresidencies.length - 1))} className="rounded-lg border border-slate-700 px-4 py-2 text-xs font-black uppercase">Siguiente <ArrowRight className="inline" size={14} /></button>
              <button onClick={() => setPresentationVpIndex(null)} className="rounded-lg border border-slate-700 px-4 py-2 text-xs font-black uppercase">Panorama</button>
            </div>
            <select value={presentationVpIndex === null ? 'PANORAMA' : String(presentationVpIndex)} onChange={(event) => setPresentationVpIndex(event.target.value === 'PANORAMA' ? null : Number(event.target.value))} className="rounded-lg border border-slate-700 bg-slate-950 px-4 py-2 text-xs font-black uppercase"><option value="PANORAMA">Panorama nacional</option>{filteredVicePresidencies.map((vp, index) => <option key={vp.vpId || 'NO_VP'} value={index}>{vp.vpName}</option>)}</select>
            <div className="flex gap-2"><button onClick={() => void requestFullscreen()} className="rounded-lg border border-slate-700 px-4 py-2 text-xs font-black uppercase"><Maximize2 className="inline" size={14} /> Pantalla completa</button><button onClick={() => { setPresentationMode(false); setPresentationVpIndex(null); if (document.fullscreenElement) void document.exitFullscreen(); }} className="rounded-lg bg-red-600 px-4 py-2 text-xs font-black uppercase text-white"><X className="inline" size={14} /> Salir</button></div>
          </div>
        </div>
      </div>
    );
  }

  const allWithoutObligation = executiveInstances.length > 0 && executiveInstances.every((row) => row.operationalStatus === 'SIN_OBLIGACION' || row.operationalStatus === 'SIN_META_VENCIDA');

  const topAlertRows = model.topAlerts.map((alert) => ({
    title: alert.instanceName,
    subtitle: alert.vpName,
    state: (alert.severity === 'critical' || alert.severity === 'high' ? 'CRITICAL' : alert.severity === 'medium' ? 'ATTENTION' : 'NEUTRAL') as ExecutiveSemanticState,
    badge: alert.severity === 'critical' || alert.severity === 'high' ? 'Crítico' : alert.severity === 'medium' ? 'Atención' : 'Contexto',
    tooltip: alert.message
  }));
  const advanceRows = model.topAdvances.map((row) => {
    const state = classifyPerformance(row.weightedPerformance, settings);
    return { title: row.instanceName, subtitle: row.vpName, state, badge: formatPct(row.weightedPerformance), tooltip: `${EXECUTIVE_STATE_PRESENTATION[state].label}. ${EXECUTIVE_TOOLTIPS.performance}` };
  });
  const interventionRows = model.interventionVicepresidencies.map((vp) => ({
    title: vp.vpName,
    subtitle: vp.presidentName,
    state: (vp.criticalCount > 0 ? 'CRITICAL' : 'NEUTRAL') as ExecutiveSemanticState,
    badge: criticalAlertLabel(vp.criticalCount),
    tooltip: `${vp.criticalCount} alertas críticas.`
  }));
  const exceptionRows = [
    ...model.legitimateExceptions.map((row) => {
      const state = classifyOperationalStatus(row.operationalStatus);
      return { title: row.instanceName, subtitle: row.vpName, state, badge: EXECUTIVE_STATE_PRESENTATION[state].label, tooltip: EXECUTIVE_STATE_PRESENTATION[state].description };
    }),
    ...model.pendingCapture.map((row) => ({ title: row.instanceName, subtitle: row.vpName, state: 'NO_CAPTURE' as ExecutiveSemanticState, badge: 'Sin captura', tooltip: EXECUTIVE_TOOLTIPS.noCapture }))
  ].slice(0, 5);

  return (
    <div className="space-y-4">
      <div data-pdf-control className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-950/80 p-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-sky-300">Resumen ejecutivo</div>
          <h1 className="mt-2 text-3xl font-black uppercase tracking-tight text-white">Panorama nacional</h1>
          <p className="mt-2 text-sm font-semibold text-slate-400">Lectura directiva de vicepresidencias e instancias.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => void onRefresh()} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-200"><RefreshCw size={14} /> Actualizar</button>
          <button onClick={() => { setPresentationVpIndex(null); setPresentationMode(true); }} className="inline-flex items-center gap-2 rounded-lg border border-sky-500/40 bg-sky-950 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-sky-100"><Presentation size={14} /> Modo presentación</button>
          <button onClick={() => void exportPdf()} disabled={exporting} className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"><Download size={14} /> {exporting ? 'Generando PDF' : 'PDF ejecutivo'}</button>
        </div>
      </div>

      {exportError && <div className="rounded-lg border border-red-500/40 bg-red-950 p-4 text-sm font-bold text-red-100"><SemanticBadge state="TECHNICAL_ERROR" /> <span className="ml-2">{exportError}</span></div>}

      <div data-pdf-control className="grid gap-3 rounded-xl border border-slate-800 bg-slate-950/70 p-4 md:grid-cols-2 xl:grid-cols-5">
        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Periodo
          <select value={OPERATIONAL_MONTHS[parseSelectedMonthIndex(selectedMonth)]} onChange={(event) => onSelectedMonthChange(OPERATIONAL_MONTH_LABELS[event.target.value as keyof typeof OPERATIONAL_MONTH_LABELS])} className="mt-2 h-11 w-full rounded-lg border border-slate-700 bg-black px-3 text-xs font-black text-white">
            {OPERATIONAL_MONTHS.map((month) => <option key={month} value={month}>{OPERATIONAL_MONTH_LABELS[month]} {data.year}</option>)}
          </select>
        </label>
        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Vicepresidencia
          <select value={vpFilter} onChange={(event) => setVpFilter(event.target.value)} className="mt-2 h-11 w-full rounded-lg border border-slate-700 bg-black px-3 text-xs font-black text-white"><option value="ALL">Todas</option>{model.vicePresidencies.map((vp) => <option key={vp.vpId || 'NO_VP'} value={vp.vpId || 'NO_VP'}>{vp.vpName}</option>)}</select>
        </label>
        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Actualización
          <select value={updateFilter} onChange={(event) => setUpdateFilter(event.target.value)} className="mt-2 h-11 w-full rounded-lg border border-slate-700 bg-black px-3 text-xs font-black text-white"><option value="ALL">Todas</option>{Object.entries(UPDATE_FILTER_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </label>
        <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Tipo
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="mt-2 h-11 w-full rounded-lg border border-slate-700 bg-black px-3 text-xs font-black text-white"><option value="ALL">Todos</option>{instanceTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select>
        </label>
        <div className="rounded-lg border border-slate-800 bg-black px-4 py-3"><div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Foco</div><div className="mt-2 text-xs font-black text-white">{focusLabel}</div><div className="mt-1 text-[10px] font-bold text-sky-300">{scopeLabel(user)}</div></div>
      </div>

      <div className="flex flex-wrap gap-2">
        {allWithoutObligation && <SemanticBadge state="NO_OBLIGATION" title={EXECUTIVE_TOOLTIPS.noObligation} />}
        {model.national.noConfigurationInstances > 0 && <SemanticBadge state="NOT_CONFIGURED" label={`${model.national.noConfigurationInstances} sin configurar`} title={EXECUTIVE_TOOLTIPS.notConfigured} />}
      </div>

      <div data-pdf-offscreen-host aria-hidden="true" style={{ position: 'fixed', left: '-20000px', top: 0, width: EXECUTIVE_PDF_LAYOUT.surfaceWidthPx, pointerEvents: 'none', zIndex: -1 }}>
        <ExecutiveOperationalSummaryPdf
          ref={pdfRef}
          model={model}
          settings={settings}
          periodLabel={`Periodo ${OPERATIONAL_MONTH_LABELS[OPERATIONAL_MONTHS[parseSelectedMonthIndex(selectedMonth)]]} ${data.year}`}
          scope={scopeLabel(user)}
          generatedAt={new Date(data.generatedAt).toLocaleString('es-MX')}
          version={APP_VERSION}
          priorities={topAlertRows}
          advances={advanceRows}
          exceptions={exceptionRows}
        />
      </div>

      <div data-executive-dashboard-root className="space-y-4 rounded-2xl bg-slate-950 p-4 text-white lg:p-5">
        <header data-pdf-header className="flex flex-col gap-3 border-b border-slate-800 pb-4 lg:flex-row lg:items-start lg:justify-between">
          <div><div className="text-[10px] font-black uppercase tracking-[0.25em] text-sky-300">Gobernanza Institucional</div><h2 className="mt-2 text-3xl font-black uppercase tracking-tight">Control Operativo · Resumen Ejecutivo</h2><p className="mt-2 text-sm font-bold text-slate-300">Periodo {OPERATIONAL_MONTH_LABELS[OPERATIONAL_MONTHS[parseSelectedMonthIndex(selectedMonth)]]} {data.year} · {scopeLabel(user)} · {focusLabel}</p></div>
          <div className="text-right text-[10px] font-bold uppercase tracking-wider text-slate-400"><div data-pdf-version>{APP_VERSION}</div><div className="mt-1">Generado {new Date(data.generatedAt).toLocaleString('es-MX')}</div><div className="mt-1">Desarrollado por IAPRIORI</div></div>
        </header>

        <NationalOverview model={model} settings={settings} />
        <ExecutiveLegend />
        <VicePresidencyTable vicePresidencies={filteredVicePresidencies} settings={settings} expanded={expanded} onToggle={toggleExpanded} />

        <section data-pdf-decision-grid className="grid gap-3 xl:grid-cols-4">
          <DecisionCard title="Alertas prioritarias" empty="Sin alertas" rows={topAlertRows} />
          <DecisionCard title="Mejor desempeño" empty="Sin desempeño aplicable" rows={advanceRows} />
          <DecisionCard title="Vicepresidencias prioritarias" empty="Sin intervención" rows={interventionRows} />
          <DecisionCard title="Excepciones" empty="Sin excepciones" rows={exceptionRows} />
        </section>

        <footer data-pdf-note className="rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 text-[9px] font-semibold leading-relaxed text-slate-400">
          Nota: los porcentajes utilizan los umbrales vigentes de Control Operativo. Las excepciones se presentan de forma explícita y no alteran los denominadores aplicables.
        </footer>
      </div>
    </div>
  );
};

export default ExecutiveOperationalSummary;
